import styled from '@emotion/styled';
import { Suspense } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';

import type { FreeTextbooksSubtopicViewQuestionsQuery } from '__free_textbooks_generated__/FreeTextbooksSubtopicViewQuestionsQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import { Base as CustomEmptyMessage } from 'ms-pages/Teacher/components/Empty';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { font } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';

import { ProblemItem } from './ProblemItem';
import { FreeTextbooksTeaserContent } from '../FreeTextbooksTeaserContent';

const ContentWrapper = styled.div<{ obfuscated: boolean }>(
  ({ obfuscated }) =>
    obfuscated && {
      opacity: 0.3,
      pointerEvents: 'none',
    },
);

const Title = styled.h2({
  ...font.gilroyHeading,
  color: colors.cloudBurst,
});

type Props = {
  subtopicId: string;
};

const FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUESTIONS_QUERY = graphql`
  query FreeTextbooksSubtopicViewQuestionsQuery($subtopicId: ID!) {
    subtopic(id: $subtopicId) {
      id
      problemContents(limit: 4) {
        id
        estimatedCompletionTime
        problemTemplateId
        isStaticQuestion
        ...ProblemItemFreeTextbooks_problemContent
      }
    }
  }
`;

export function FreeTextbooksSubtopicViewQuestions({ subtopicId }: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSubtopicViewQuestions">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSubtopicViewQuestionsInner
            subtopicId={subtopicId}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSubtopicViewQuestionsInner({
  subtopicId,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksSubtopicViewQuestionsQuery>(
    FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUESTIONS_QUERY,
    { subtopicId },
    { fetchKey },
  );

  const { subtopic } = data;
  if (subtopic == null) return <>Subtopic not found</>; // todo better ui
  const { problemContents: questions } = subtopic;

  const interactiveQuestions = questions.filter(
    problemContent => !problemContent.isStaticQuestion,
  );

  return (
    <div style={{ height: '100%' }}>
      <Title>Interactive practice questions</Title>
      <Separator size={5} />
      {interactiveQuestions.length === 0 ? (
        <CustomEmptyMessage label="There are no interactive problems for this subtopic" />
      ) : (
        <FreeTextbooksTeaserContent
          title="Sign up to access Practice Questions"
          snowplowEventLabel="practice_access"
        >
          {interactiveQuestions.map((problemContent, index) => (
            <ContentWrapper key={problemContent.id} obfuscated={index > 0}>
              <ProblemItem
                problemContentKey={problemContent}
                startsCompressed={index > 0}
              />
              <Separator size={6} />
            </ContentWrapper>
          ))}
        </FreeTextbooksTeaserContent>
      )}
    </div>
  );
}
